import type { EnrichedError } from "@web-monorepo/telemetry";
import type { TimeZone } from "./periods.ts";
import { getLocalTimezoneOverride } from "./timezoneContext.ts";

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Both `Intl.DateTimeFormat.formatToParts` and the formatted-string fallback came back
 * incomplete (an Edge bug, in practice).
 *
 * The offending timestamp stays OUT of the message and off in `extra`: Sentry's default grouping
 * hashes the message, so interpolating it minted a brand new issue for every distinct instant this
 * fired on. That churn is invisible to any users-affected or archive-until-escalating threshold,
 * because no single issue ever accumulates volume (WEBP-184).
 *
 * "{{ default }}" keeps Sentry's default grouping components (stack trace included) in the hash
 * instead of replacing them wholesale: `dayParts` is called from dozens of unrelated call sites,
 * and collapsing all of them into one issue would erase the caller identity needed to triage —
 * this only needs to stop the timestamp from re-splitting the *same* call site into a fresh issue
 * per instant, not merge every call site together (see BareFetchError in packages/telemetry for
 * the same pattern). */
export class DatePartsParseError extends Error implements EnrichedError {
  extra: Record<string, unknown>;
  fingerprint = ["{{ default }}", "date-parts-parse-error"];

  constructor(date: Date, timeZone?: TimeZone) {
    super("Failed to parse date parts");
    this.name = "DatePartsParseError";
    this.extra = { date: date.toISOString(), timeZone: timeZone ?? "(browser default)" };
  }
}

// eslint-disable-next-line complexity -- TODO: refactor to reduce complexity
export function dayParts(date: Date, timeZone?: TimeZone | null) {
  timeZone ??= getLocalTimezoneOverride() || undefined;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    weekday: "short",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const partsMap = new Map(parts.map((p) => [p.type, p.value]));

  let year = partsMap.get("year");
  let month = partsMap.get("month");
  let day = partsMap.get("day");
  let hours = partsMap.get("hour");
  let minutes = partsMap.get("minute");
  let seconds = partsMap.get("second");
  let weekday = partsMap.get("weekday");
  let milliseconds = partsMap.get("fractionalSecond");

  if (!year || !month || !day || !hours || !minutes || !seconds || !weekday) {
    // Fallback: parse the formatted string when formatToParts returns incomplete results (Edge browser bug)
    // Accepts padded and non-padded variants; fractional seconds are tenths/hundredths/milliseconds, so "5" becomes "500".
    const formatted = formatter.format(date);
    const match = formatted.match(
      /^(?<weekday>\w+), (?<month>\d{1,2})\/(?<day>\d{1,2})\/(?<year>\d{4}), (?<hours>\d{1,2}):(?<minutes>\d{1,2}):(?<seconds>\d{1,2})(?:\.(?<milliseconds>\d{1,3}))?$/,
    );
    if (match?.groups) {
      weekday ??= match.groups.weekday;
      month ??= match.groups.month.padStart(2, "0");
      day ??= match.groups.day.padStart(2, "0");
      year ??= match.groups.year;
      hours ??= match.groups.hours.padStart(2, "0");
      minutes ??= match.groups.minutes.padStart(2, "0");
      seconds ??= match.groups.seconds.padStart(2, "0");
      milliseconds ??= match.groups.milliseconds?.padEnd(3, "0");
    }
  }

  if (!year || !month || !day || !hours || !minutes || !seconds || !weekday) {
    throw new DatePartsParseError(date, timeZone);
  }

  const weekdayIndex = weekdays.indexOf(weekday);

  return {
    year,
    month,
    day,
    hours,
    minutes,
    seconds,
    milliseconds: milliseconds || String(date.getMilliseconds()).padStart(3, "0"),
    weekday: weekdayIndex === -1 ? date.getDay() : weekdayIndex,
  };
}
